import type { Scalars } from '@varo/graphql-schema-types';
import { DELETE } from 'shared/components/vxl/NumberPad/NumberPadButton';

const AMOUNT_PATTERN = /^(\d+)(?:\.(\d{1,2})0*)?$/;
const DIGIT_KEY_PATTERN = /^\d$/;
const DOUBLE_ZERO_KEY = '00';
const BACKSPACE_KEY = 'Backspace';
const MINOR_UNITS_PER_MAJOR_UNIT = 100;
const MINOR_UNIT_DIGITS = 2;
const DIGIT_SHIFT_FACTOR = 10;
const MAX_INTEGER_DIGITS_BEFORE_SHIFT = 6;

/**
 * Converts a plain decimal amount string (for example "10.05") into exact
 * integer minor units (cents). Trailing zeros past the second fractional digit
 * are dropped ("10.500" -> 1050). Returns undefined when the string is not of
 * the form /^\d+(\.\d{1,2}0*)?$/ or the result is not a safe integer.
 */
export const parseAmountToMinorUnits = (amount: string): number | undefined => {
  const match = AMOUNT_PATTERN.exec(amount);
  if (!match) {
    return undefined;
  }

  const [, integerDigits, fractionDigits = ''] = match;
  const minorUnits = Number(
    `${integerDigits}${fractionDigits.padEnd(MINOR_UNIT_DIGITS, '0')}`,
  );

  return Number.isSafeInteger(minorUnits) ? minorUnits : undefined;
};

/**
 * Formats exact non-negative integer minor units (cents) as a "X.XX" amount
 * string. Returns undefined when given a value that is negative or not a safe
 * integer.
 */
export const formatMinorUnitsAsAmount = (
  minorUnits: number,
): string | undefined => {
  if (!Number.isSafeInteger(minorUnits) || minorUnits < 0) {
    return undefined;
  }

  const digits = String(minorUnits).padStart(MINOR_UNIT_DIGITS + 1, '0');
  const integerDigits = digits.slice(0, -MINOR_UNIT_DIGITS);
  const fractionDigits = digits.slice(-MINOR_UNIT_DIGITS);

  return `${integerDigits}.${fractionDigits}`;
};

/**
 * Converts a server Money.amount (generated Decimal scalar) into exact integer
 * minor units. The value is stringified and run through the lexical parser, so
 * non-finite numbers ("NaN", "Infinity"), negative values, exponent notation
 * ("1e+21"), and values with non-zero digits past the second fractional digit
 * (including float artifacts such as 0.30000000000000004) return undefined.
 */
export const moneyAmountToMinorUnits = (
  amount: Scalars['Decimal'],
): number | undefined => parseAmountToMinorUnits(String(amount));

/**
 * Normalizes an amount from any external source (server Decimal, URL param,
 * etc.) to the canonical "X.XX" form used by applyKeyToTransferAmount, dropping
 * extra trailing zeros ("10.500" -> "10.50"). Returns undefined when the amount
 * is not a valid non-negative amount with at most two significant fractional
 * digits.
 */
export const normalizeAmount = (
  amount: Scalars['Decimal'],
): string | undefined => {
  const minorUnits = moneyAmountToMinorUnits(amount);
  return minorUnits === undefined
    ? undefined
    : formatMinorUnitsAsAmount(minorUnits);
};

/**
 * Moves a "X.XX" amount to the closest value within the minimum and maximum.
 * The maximum wins when it is below the minimum. Amounts that cannot be parsed
 * are returned unchanged.
 */
export const clampAmountToRange = (
  amount: string,
  minimumMinorUnits: number | undefined,
  maximumMinorUnits: number | undefined,
): string => {
  const minorUnits = parseAmountToMinorUnits(amount);
  if (minorUnits === undefined) {
    return amount;
  }
  const raisedMinorUnits = Math.max(
    minorUnits,
    minimumMinorUnits ?? minorUnits,
  );
  const clampedMinorUnits = Math.min(
    raisedMinorUnits,
    maximumMinorUnits ?? raisedMinorUnits,
  );
  return formatMinorUnitsAsAmount(clampedMinorUnits) ?? amount;
};

const countIntegerDigits = (minorUnits: number) =>
  String(Math.floor(minorUnits / MINOR_UNITS_PER_MAJOR_UNIT)).length;

const shiftInDigit = (
  minorUnits: number | undefined,
  digit: number,
): number | undefined =>
  minorUnits !== undefined &&
  countIntegerDigits(minorUnits) <= MAX_INTEGER_DIGITS_BEFORE_SHIFT
    ? minorUnits * DIGIT_SHIFT_FACTOR + digit
    : undefined;

/**
 * Exact, cents-based replacement for applyKeyToAmount. Returns the next
 * "X.XX" amount for a keypad key, or undefined when the key press should not
 * change the amount (unsupported key, unparseable amount, or digit cap reached).
 *
 * - Digit keys shift the amount left and append the digit.
 * - "00" shifts in two zeros.
 * - "Backspace" / DELETE shift the amount right, dropping the last digit, and
 *   reset an unparseable amount to "0.00" so the user can always recover.
 * - An empty amount is treated as "0.00".
 */
export const applyKeyToTransferAmount = (
  amount: string,
  key: string,
): string | undefined => {
  const currentMinorUnits = amount === '' ? 0 : parseAmountToMinorUnits(amount);
  const isBackspaceKey = key === DELETE || key === BACKSPACE_KEY;
  if (currentMinorUnits === undefined) {
    return isBackspaceKey ? formatMinorUnitsAsAmount(0) : undefined;
  }

  let nextMinorUnits: number | undefined;
  if (isBackspaceKey) {
    nextMinorUnits = Math.floor(currentMinorUnits / DIGIT_SHIFT_FACTOR);
  } else if (key === DOUBLE_ZERO_KEY) {
    nextMinorUnits = shiftInDigit(shiftInDigit(currentMinorUnits, 0), 0);
  } else if (DIGIT_KEY_PATTERN.test(key)) {
    nextMinorUnits = shiftInDigit(currentMinorUnits, Number(key));
  }

  return nextMinorUnits === undefined
    ? undefined
    : formatMinorUnitsAsAmount(nextMinorUnits);
};
